import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, MapPin, Pencil, EyeOff, PartyPopper } from "lucide-react";
import { imgSrc } from "@/lib/img";
import { PageTitle } from "@/components/SettingsPanel";
import { QrPosterButton } from "@/components/QrPoster";
import { WelcomeGuide } from "@/components/WelcomeGuide";
import { TypeBadge, StatusPill } from "@/components/VenueBits";
import { SpotForm } from "@/components/SpotForm";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { Venue, User } from "@/types";
import { useLive } from "@/lib/live";

export default function VenueMyVenues() {
  const { t, lang } = usePrefs();
  const user = useAuth().user as User;
  const [list, setList] = useState<Venue[] | null>(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/venue/venues").then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="venue-my-venues-page">
      <PageTitle
        title={t("myVenues.title")}
        sub={t("myVenues.sub")}
        action={
          user.venue_role === "owner" && user.venue_account?.status === "approved" && (
            <button data-testid="add-venue-button" onClick={() => setAdding(true)} className="h-11 px-5 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {t("myVenues.add")}
            </button>
          )
        }
      />
      {list && user.venue_role === "owner" && user.venue_account?.status === "approved" && <WelcomeGuide user={user} venues={list} onAddVenue={() => setAdding(true)} />}
      {list?.length === 0 && <p className="text-muted-foreground py-10 text-center">{t("myVenues.empty")}</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        {list?.map((v) => (
          <div key={v.venue_id} data-testid={`my-venue-${v.venue_id}`} className={`rounded-3xl border bg-card p-3 flex flex-col sm:flex-row gap-4 ${(v.review_status || "approved") !== "approved" ? "border-dashed" : ""}`}>
            <img src={imgSrc(v.image)} alt="" className={`h-36 sm:h-auto sm:w-32 rounded-2xl object-cover ${(v.review_status || "approved") !== "approved" ? "grayscale opacity-60" : ""}`} />
            <div className="flex-1 space-y-3 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <TypeBadge type={v.type} />
                {v.review_status && v.review_status !== "approved" && <>
                  <span data-testid={`venue-hidden-badge-${v.venue_id}`} className="inline-flex items-center gap-1 rounded-full bg-zinc-500/20 text-zinc-700 dark:text-zinc-300 px-2.5 py-0.5 text-xs font-bold"><EyeOff className="h-3 w-3" />{t("vr.hidden")}</span>
                  <StatusPill status={v.review_status} />
                </>}
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{v.city}</span>
                {(v.review_status || "approved") === "approved" && <span data-testid={`venue-events-flag-${v.venue_id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${v.events_enabled ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground"}`}><PartyPopper className="h-3 w-3" />{t(v.events_enabled ? "pp.eventsOn" : "pp.eventsOff")}</span>}
                {user.venue_role === "owner" && user.venue_account?.status === "approved" && <Link to={`/venue/venues/${v.venue_id}/edit`} data-testid={`edit-venue-${v.venue_id}`} className="ms-auto h-9 px-3 inline-flex items-center gap-1.5 rounded-full bg-accent text-xs font-bold hover:bg-primary hover:text-primary-foreground transition-[background-color,color]">
                  <Pencil className="h-3.5 w-3.5" />
                  {t("myVenues.edit")}
                </Link>}
              </div>
              <p className="font-display font-bold text-lg leading-snug">{lang === "ar" ? v.name_ar : v.name_en || v.name_ar}</p>
              {v.review_status === "pending" && <p data-testid={`venue-review-note-${v.venue_id}`} className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-3 py-2 text-xs font-semibold">{t("vr.pendingNote")}</p>}
              {v.review_status === "rejected" && <p data-testid={`venue-review-note-${v.venue_id}`} className="rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-400 px-3 py-2 text-xs font-semibold">{t("vr.rejectedNote").replace("{r}", v.review_reason || "")}</p>}
              {v.edit_status === "pending" && <p data-testid={`venue-edit-note-${v.venue_id}`} className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-3 py-2 text-xs font-semibold">{t("pp.editPending")}</p>}
              {v.edit_status === "rejected" && <p data-testid={`venue-edit-note-${v.venue_id}`} className="rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-400 px-3 py-2 text-xs font-semibold">{t("pp.editRejected").replace("{r}", v.edit_reason || "")}</p>}
              {typeof v.lat !== "number" && <p data-testid={`venue-no-location-${v.venue_id}`} className="rounded-2xl bg-amber-500/15 text-amber-700 dark:text-amber-400 px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0" />{t("pp.noLocationPartner")}</p>}
              {["owner", "reception"].includes(user.venue_role || "") && user.venue_account?.status === "approved" && (v.review_status || "approved") === "approved" && <QrPosterButton v={v} />}
            </div>
          </div>
        ))}
      </div>
      {adding && <SpotForm spot={null} partner onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}
